import {
  ChatMessage,
  ChatMessageBuilder,
  ChatMessageTypes,
  ComposedPage,
  HandshakeMode,
  HandshakeResponse,
  HandshakeTokenPayload,
  InteractionTypes,
  SurveyLoginType,
  SurveyResolverStartPayload,
} from '@hyper-inferno/survey-elements-core-utils';
import { PageType, SurveyVariable } from '@hyper-inferno/survey-elements-core';

// Handlers are typically heavy; ensure these are accessible in both environments

import { v4 as uuidv4 } from 'uuid';
import { SurveyBotEngine } from './survey-engine/survey-engine.class';
import {
  SurveySessionStatus,
  SyncStatus,
} from './survey-session/survey-session.interface';
// ... import other handlers

/**
 * Universal Survey Orchestrator
 * This class handles the state machine and logic.
 * Concrete implementations (Nest or Angular) handle the "Storage" and "HTTP".
 */
export abstract class AbstractSurveyService {
  async getHandshakeToken(
    body: SurveyResolverStartPayload,
    origin: string,
  ): Promise<HandshakeResponse> {
    // 1. Origin Protection (Simple CORS-like check)
    // const allowedOrigins = process.env.ALLOWED_ORIGINS?.split(',') || [
    //   'http://localhost:4300',
    // ];
    // if (!allowedOrigins.includes(origin)) {
    //   // throw new ForbiddenException('Unauthorized Origin');
    // }

    const loginType = body.loginType; // Now explicitly passed from Angular Resolver
    let resolvedSurveyId;
    switch (loginType) {
      case SurveyLoginType.SESSION: {
        // const session = await this.repo.getSessionById(body.sessionId);
        // if (!session)
        //   throw new NotFoundException('Session expired or not found');
        // resolvedSurveyId = session.survey_id;
        break;
      }

      case SurveyLoginType.INVITATION: {
        // const invite = await this.repo.getInvitationByGuid(body.invitationId);
        // if (!invite || invite.used)
        //   throw new UnauthorizedException('Invitation invalid or already used');
        // resolvedSurveyId = invite.survey_id;
        break;
      }

      case SurveyLoginType.SELF_SERVE:
      default:
        resolvedSurveyId = body.surveyId;
        // For self-serve, we MUST have a surveyId in the payload
        if (!resolvedSurveyId)
          throw new Error('Survey ID required for self-serve');
        break;
    }

    if (!resolvedSurveyId) {
      throw new Error('Handshake failed to resolve Survey context.');
    }

    const survey = {}; // TODO check if this survey exists
    if (!survey) {
      throw new Error('Survey resource not found');
    }
    // 3. ACCESS & COOKIE LOGIC (Incorporating your old Middleware logic)
    // const accessDetails =
    //   await this.repo.getSurveyAccessDetails(resolvedSurveyId);

    // Check for existing cookie restart (even if loginType was 'self-serve')
    // const signedSessionId = req.cookies?.[sessionCookieName];
    // if (accessDetails.cookieBasedRestart && signedSessionId) {
    //   return this.issueHandshake(
    //     resolvedSurveyId,
    //     signedSessionId,
    //     SurveyLoginType.SESSION,
    //     HandshakeMode.RESTART,
    //     body,
    //   );
    // }

    // 4. LOGIN / CONSENT (Piping logic)
    //   if (accessDetails.accessType === SurveyLoginType.LOGIN) {

    //   // CASE A: User just arrived, hasn't logged in yet
    //   if (!body.isLogin) {
    //     const pipedPage = await this.pipingService.pipeForLoginPage(...);
    // return { mode: HandshakeMode.NEEDS_LOGIN, consentMessage: pipedPage };
    //   }
    const survey_variables =
      (await this.getVariablesFromVersion(Number(resolvedSurveyId))) ?? [];

    // 1. Find the specific object where is_unique_id is true
    const uniqueVariable = survey_variables.find(
      (item) => item.is_unique_id === true,
    );

    if (uniqueVariable) {
      // Make sure the variable exists in the link
      const variableName = uniqueVariable?.variable_name;
      const urlParams = (body.params as any)?.urlQueryParams || {};
      // Use the variable_name as the key to look up in your URL params
      const password = variableName ? urlParams[variableName] : '';
      let handledPassword = password;
      if (!password) {
        handledPassword = await this.handleNoPassword(variableName);

        if (!handledPassword) throw new Error(`${variableName} not provided`);
      }

      // Check if an active password is associated with this session
      const activeSessionId = await this.verifyIfPasswordHasSession(
        resolvedSurveyId,
        variableName,
        handledPassword,
      );
      if (!(body.params as any)?.urlQueryParams) {
        (body.params as any) = {
          ...(body.params as any),
          urlQueryParams: { [variableName]: handledPassword },
        };
      }

      if (activeSessionId) {
        // continue
        const handshakeToken = await this.getHandshakeTokenString({
          surveyId: resolvedSurveyId,
          loginType: loginType, // <--- EXPLICITLY SAVED IN TOKEN
          sessionId: activeSessionId.id ?? null,
          invitationId: body.invitationId || undefined,
          params: body,
          session_unique_identifier: handledPassword,
        });
        return {
          mode: HandshakeMode.AUTHORIZED,
          handshakeToken,
          serverTime: new Date().getMilliseconds(),
        };
      } else {
        return this.generateNewSessionWithAuthorizedHandshake(
          resolvedSurveyId,
          loginType,
          body,
        );
      }
    }

    // 2. Safely extract the variable name, or fallback if not found
    // const isAuthenticated = await this.passwordLogin(
    //   password,
    //   uniqueVariable,
    //   Number(resolvedSurveyId),
    // );
    // Does this survey have a login screen?
    // if (isAuthenticated === true) {
    //   const loginT = await this.surveyLogin(resolvedSurveyId);
    //   if (loginT) {
    //     return {
    //       mode: HandshakeMode.NEEDS_LOGIN,
    //       consentMessage: new ChatMessageBuilder()
    //         .setPage(loginT)
    //         .setSender(String(resolvedSurveyId))
    //         .build(),
    //       serverTime: new Date().getMilliseconds(),
    //       consentInitToken: JSON.stringify(body),
    //     };
    //   }
    // }

    // 5. ISSUE THE AUTHORIZED HANDSHAKE TOKEN
    // We include loginType in the JWT so /start knows exactly what happened here
    let resolvedSessionId;
    if (!body.sessionId) {
      resolvedSessionId = uuidv4();
    }
    const handshakeToken = await this.getHandshakeTokenString({
      surveyId: resolvedSurveyId,
      loginType: loginType, // <--- EXPLICITLY SAVED IN TOKEN
      sessionId: resolvedSessionId ?? null,
      invitationId: body.invitationId || undefined,
      params: body,
    });

    return {
      mode: HandshakeMode.AUTHORIZED,
      handshakeToken,
      serverTime: new Date().getMilliseconds(),
    };
  }

  async generateNewSessionWithAuthorizedHandshake(
    resolvedSurveyId: number,
    loginType: SurveyLoginType,
    body: Record<string, any>,
  ) {
    // let resolvedSessionId;
    // if (!body.sessionId) {
    const resolvedSessionId = uuidv4();
    // }

    const handshakeToken = await this.getHandshakeTokenString({
      surveyId: resolvedSurveyId,
      loginType: loginType, // <--- EXPLICITLY SAVED IN TOKEN
      sessionId: resolvedSessionId ?? null,
      // invitationId: body.invitationId || undefined,
      invitationId: undefined,
      params: body,
    });

    return {
      mode: HandshakeMode.AUTHORIZED,
      handshakeToken,
      serverTime: new Date().getMilliseconds(),
    };
  }

  // --- Abstract Data Access Methods ---

  // protected abstract surveyLogin(
  //   surveyId: number,
  // ): Promise<ComposedPage | undefined>;
  protected abstract getVariablesFromVersion(
    survey_id: number,
  ): Promise<SurveyVariable[]>;
  protected abstract verifyIfPasswordHasSession(
    survey_id: number,
    passKey: string,
    passValue: string,
  ): Promise<any>;

  protected abstract handleNoPassword(variableName: string): Promise<string>;

  // Children will implement these using Supabase, HttpClient, or LocalStorage
  protected abstract getHandshakeTokenString(
    handshakeTokenPayload: HandshakeTokenPayload,
  ): string;

  //   protected abstract getSurveyDefinition(surveyId: number): Promise<any>;
  //   protected abstract saveSessionState(sessionData: any): Promise<boolean>;
  //   protected abstract updateSessionResponses(
  //     sessionId: string,
  //     surveyId: string,
  //     responses: any,
  //   ): Promise<any>;

  protected abstract getEngine(
    session_id: string,
    params?: any,
  ): Promise<SurveyBotEngine>;

  protected abstract createSession(arg0: {
    id: string;
    survey_id: number;
    sys_survey_start_time: string;
    jsn_responses: {};
    sys_last_updated: string;
    session_status: SurveySessionStatus;
    updated_at: string;
    sync_status: SyncStatus;
    sys_profile_identifier: string;
    survey_language: string;
    session_unique_identifier: string;
  }): Promise<any>;

  /**
   * Orchestrates the start of a survey session.
   */
  async startSession(
    surveyId: string,
    sessionId: string,
    params: any,
    user: string,
  ): Promise<ChatMessage> {
    const uniqueIdentifier = params?.params?.urlQueryParams?.password;
    await this.createSession({
      id: sessionId,
      survey_id: Number(surveyId),
      sys_survey_start_time: new Date().toISOString(),
      jsn_responses: {},
      sys_last_updated: new Date().toISOString(),
      session_status: SurveySessionStatus.IN_PROGRESS,
      updated_at: new Date().toISOString(),
      sync_status: SyncStatus.DIRTY,
      sys_profile_identifier: user,
      survey_language: params.survey_language,
      session_unique_identifier: uniqueIdentifier,
    });
    const bot = await this.getEngine(sessionId, params);

    if (!bot) {
      throw new Error('Failed to retrieve bot');
    }
    const firstPage = bot.getFirstPage();

    if (!firstPage) {
      throw new Error('Could not determine First Page');
    }

    const firstPageRender = await bot.renderPage(firstPage.id, 'start');

    if (!firstPageRender) {
      throw new Error('Could not Render First Page');
    }

    const globalPlugins = bot.getGlobalPlugins(Number(surveyId));

    await bot.saveState();

    const _chatMessage = new ChatMessageBuilder()
      .setMessageType(ChatMessageTypes.Page)
      .setPage(firstPageRender)
      .setRecipient(sessionId)
      .setSender(surveyId)
      .setGlobalPlugins(globalPlugins);

    switch (firstPageRender.type) {
      case PageType.ENDING_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.EndPage);
        break;

      case PageType.ELEMENT_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.Page);
        break;

      default:
        break;
    }

    return _chatMessage.build();
  }

  async handleMessage(
    chatMessage: ChatMessage,
    sessionId: string,
  ): Promise<ChatMessage> {
    switch (chatMessage.interactiveMessage?.type) {
      // case InteractionTypes.SKIP:
      //   return this.handleSkip(sessionId, chatMessage);

      case InteractionTypes.SWITCH:
        return this.handleSwitch(sessionId, chatMessage);

      // case InteractionTypes.LANGUAGE:
      //   return this.handleLanguageSwitch(sessionId, chatMessage);

      case InteractionTypes.CUSTOM_SWITCH:
        return this.handleCustomSwitch(sessionId, chatMessage);

      case InteractionTypes.SUBMIT:
        return this.handleSubmit(sessionId, chatMessage);

      default:
        return chatMessage;
    }
  }

  /**
   * Orchestrates the submission of an answer and movement to the next page.
   */
  private async handleSubmit(
    sessionId: string,
    chatMessage: ChatMessage,
  ): Promise<ChatMessage | PromiseLike<ChatMessage>> {
    const bot = await this.getEngine(
      sessionId,
      chatMessage.interactiveMessage?.submitAnswerPayload?.answerObject,
    );

    bot.addOEMapping(
      chatMessage.interactiveMessage?.submitAnswerPayload?.oe_mappings ?? {},
    );

    // In Current Page, check if any of the Plugins have presave hook.
    // If yes execute them
    // else continue

    if (chatMessage.interactiveMessage?.languageChangePayload?.language) {
      bot.changeLanguage(
        chatMessage.interactiveMessage?.languageChangePayload?.language,
      );
    }

    const nextPage = await bot.nextPage();
    const nextPageRender = await bot.renderPage(nextPage.id, 'forward');
    await bot.saveState();

    const _chatMessage = new ChatMessageBuilder()
      .setPage(nextPageRender)
      .setRecipient(sessionId)
      .setSender(bot.surveyId);

    switch (nextPageRender.type) {
      case PageType.ENDING_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.EndPage);
        break;

      case PageType.ELEMENT_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.Page);
        break;

      default:
        break;
    }

    return _chatMessage.build();
  }

  private async handleCustomSwitch(
    sessionId: string,
    chatMessage: ChatMessage,
  ): Promise<ChatMessage | PromiseLike<ChatMessage>> {
    const bot = await this.getEngine(sessionId, {});

    // bot.addOEMapping(
    //   chatMessage.interactiveMessage?.submitAnswerPayload?.oe_mappings ?? {},
    // );

    const jumptTarget =
      chatMessage.interactiveMessage?.customSwitchQuestionPayload?.pageId;

    const nextPageRender = await bot.renderPage(jumptTarget, 'jump');
    await bot.saveState();
    const _chatMessage = new ChatMessageBuilder()
      .setPage(nextPageRender)
      .setRecipient(sessionId)
      .setSender(bot.surveyId);

    switch (nextPageRender.type) {
      case PageType.ENDING_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.EndPage);
        break;

      case PageType.ELEMENT_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.Page);
        break;

      default:
        break;
    }

    return _chatMessage.build();
  }

  private async handleSwitch(
    sessionId: string,
    chatMessage: ChatMessage,
  ): Promise<ChatMessage | PromiseLike<ChatMessage>> {
    const bot = await this.getEngine(sessionId, {});

    // bot.addOEMapping(
    //   chatMessage.interactiveMessage?.submitAnswerPayload?.oe_mappings ?? {},
    // );

    const direction = (chatMessage.interactiveMessage as any)
      .switchQuestionPayload.isNext
      ? 'forward'
      : 'back';

    let navigationTargetPage;

    switch (direction) {
      case 'forward':
        break;
      case 'back':
        navigationTargetPage = bot.previousPage();
        break;

      default:
        break;
    }

    const jumptTarget =
      chatMessage.interactiveMessage?.switchQuestionPayload?.isNext;

    const nextPageRender = await bot.renderPage(
      navigationTargetPage,
      direction,
    );
    await bot.saveState();
    const _chatMessage = new ChatMessageBuilder()
      .setPage(nextPageRender)
      .setRecipient(sessionId)
      .setSender(bot.surveyId);

    switch (nextPageRender.type) {
      case PageType.ENDING_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.EndPage);
        break;

      case PageType.ELEMENT_PAGE_MODEL:
        _chatMessage.setMessageType(ChatMessageTypes.Page);
        break;

      default:
        break;
    }

    return _chatMessage.build();
  }

  async getDynamicOptions(message: { props: any; page_value: any }) {
    const engine = await this.getEngine(message.props.sender, {});
    return engine.getDynamicOptions(message.props.list_id, message.page_value);
  }
}
